import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, ExternalLink } from "lucide-react";
import { TorriLogo } from "@/components/TorriLogo";
import ElectricVesta from "@/pages/vesta/HearthVesta";

const HERO = "/login-hero.jpg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="mb-10"><TorriLogo imgClass="h-[52px]" /></div>

          <h1 className="mb-3 mt-2 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
            Accedi alla piattaforma online dedicata alla <span className="text-primary">rete agenzie e partner</span>.
          </h1>
          <p className="mb-2 text-sm text-muted-foreground">
            Con le tecnologie fotovoltaiche Made in Italy <span className="font-semibold text-foreground">TORRI</span><span className="font-semibold text-primary">.solar</span> sei parte di un importante progetto di cultura industriale e tecnologica.
          </p>
          <p className="mb-8 text-sm text-muted-foreground">
            In pochi minuti, ovunque tu sia, puoi accedere al tuo portafoglio, alla tua agenda ed ai dati analitici della tua area.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@torri.solar" required data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password" />
            </div>
            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading}
              className="w-full rounded-full py-6 text-sm font-bold" data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Accedi alla piattaforma <ArrowRight className="ml-1 h-4 w-4" /></>)}
            </Button>
          </form>

          <div className="my-7 flex items-center gap-3 text-[11px] uppercase tracking-widest text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> oppure <span className="h-px flex-1 bg-border" />
          </div>
          <Link to="/vesta" data-testid="login-vesta-link"
            className="group flex w-full items-center justify-center gap-2 rounded-full border border-[#C17F59] px-5 py-3.5 text-sm font-bold text-[#C17F59] transition-colors duration-200 hover:bg-[#C17F59] hover:text-black">
            Sei un Partner? Entra in <span className="text-[13px] font-extrabold tracking-[0.2em]" data-testid="login-vesta-electric"><ElectricVesta /></span> <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>

      <div className="relative hidden overflow-hidden lg:block" data-testid="login-hero">
        <img src={HERO} alt="Fabbrica di pannelli fotovoltaici TORRI.solar" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">La nostra azienda</div>
          <p className="max-w-lg text-xl font-bold leading-snug text-foreground">
            Dal 2007, la prima fabbrica di pannelli fotovoltaici Made in Italy con la garanzia del chiavi in mano diretto al cliente finale.
          </p>
          <a href="https://www.torri.solar/" target="_blank" rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            Scopri le nostre tecnologie su www.torri.solar <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
